import {
  Component,
  OnInit,
  TemplateRef,
  ViewChild,
  OnChanges,
  SimpleChanges,
  Input,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCardModule } from '@angular/material/card';
import { NgToastService } from 'ng-angular-popup';
import { MatMenuModule } from '@angular/material/menu';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { ApiService } from '@/app/services/shared/api.service';
import { MatGridListModule } from '@angular/material/grid-list';

import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { RoleNamesPipe } from '@/app/pipes/role-names.pipe';
export enum USER_ROLE_MESSAGES {
  ADD_USER = 'User added successfully!',
  REQUIRED_FIELDS_MISSING = 'Fill all the require fields',
  UPDATE_USER = 'User updated successfully!',
  DELETE_USER = 'User deleted successfully!',
}
@Component({
  selector: 'app-users',
  imports: [
    CommonModule,
    MatTabsModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCardModule,
    DragDropModule,
    ReactiveFormsModule,
    MatMenuModule,
    RoleNamesPipe,
    MatGridListModule,
  ],
  standalone: true,
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.css'],
})
export class UsersComponent implements OnInit, OnChanges {
  @ViewChild('addUserDialog') addUserDialog!: TemplateRef<any>;
  @Input() isDialogOpen!: boolean;
  @Input() selectedTab!: string;
  users: any[] = [];
  roles: any[] = [];
  editUserId: number;
  // user form controls
  userForm = new FormGroup({
    firstName: new FormControl('', Validators.required),
    lastName: new FormControl('', Validators.required),
    telephone: new FormControl('', [
      Validators.required,
      Validators.pattern(/^[0-9]{10,15}$/),
    ]),
    email: new FormControl('', [Validators.required, Validators.email]),
    roles: new FormControl('', Validators.required),
    password: new FormControl('', Validators.required),
    confirmPassword: new FormControl('', Validators.required),
  });
  org_info: any = localStorage.getItem('org_info')
    ? JSON.parse(localStorage.getItem('org_info') as string)
    : null;
  constructor(
    private apiService: ApiService,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private toast: NgToastService
  ) {}

  ngOnInit(): void {
    this.loadUsers();
    this.loadRoles();
  }
  ngOnChanges(changes: SimpleChanges) {
    if (
      changes['isDialogOpen'] &&
      !changes['isDialogOpen'].firstChange &&
      this.selectedTab === 'users'
    ) {
      this.editUserId = null;
      this.userForm.reset();
      this.openAddUserDialog();
    }
  }
  openAddUserDialog() {
    this.dialog.open(this.addUserDialog, {
      disableClose: true,
      autoFocus: false,
    });
  }
  loadUsers() {
    this.apiService.getUsers().subscribe((data) => (this.users = data['data']));
  }
  loadRoles() {
    this.apiService.getRoles().subscribe((data) => {
      this.roles = data;
    });
  }
  // Handle user form
  async createUser() {
    const dataToSubmit: any = await this.prepareUserDataToSubmit();
    if (this.editUserId) {
      this.apiService.updateUser(dataToSubmit, this.editUserId).subscribe({
        next: (response: any) => {
          if (response.meta.success) {
            this.toast.success(USER_ROLE_MESSAGES.UPDATE_USER, 'SUCCESS', 4000);
            this.userForm.reset();
            this.loadUsers();
            this.closeDialog();
          }
        },
        error: (error: any) => {
          this.toast.danger(error.error.message, 'DANGER', 4000);
        },
      });
    } else {
      this.apiService.createUser(dataToSubmit).subscribe({
        next: (response: any) => {
          if (response.meta.success) {
            this.toast.success(USER_ROLE_MESSAGES.ADD_USER, 'SUCCESS', 4000);
            this.userForm.reset();
            this.loadUsers();
            this.closeDialog();
          }
        },
        error: (error: any) => {
          this.toast.danger(error.error.message, 'DANGER', 4000);
        },
      });
    }
  }
  async prepareUserDataToSubmit() {
    if (this.userForm.invalid) {
      this.toast.warning(
        USER_ROLE_MESSAGES.REQUIRED_FIELDS_MISSING,
        'WARNING',
        4000
      );
      this.userForm.markAllAsTouched();
      return;
    }
    const dataToSubmit = {
      organization_id: this.org_info?.id,
      name: this.userForm.value.firstName + ' ' + this.userForm.value.lastName,
      phone: this.userForm.value.telephone,
      email: this.userForm.value.email,
      password: this.userForm.value.password,
      password_confirmation: this.userForm.value.confirmPassword,
      role_ids: this.userForm.value.roles,
    };

    return dataToSubmit;
  }
  // Close the dialog
  closeDialog() {
    this.dialog.closeAll();
  }
  getRoleNames(roles: any[]): string {
    return roles?.map((p) => p.name).join(', ') || 'No roles assigned';
  }
  editUser(user: any) {
    // Patch values into the form
    this.userForm.patchValue({
      firstName: user?.name,
      lastName: user?.name,
      email: user?.email,
      telephone: user?.phone,
      roles: user.roles.map((role) => role?.id),
    });
    this.editUserId = user?.id;
    this.dialog.open(this.addUserDialog);
  }
  deleteUser(userId: number) {
    this.apiService.deleteUser(userId).subscribe({
      next: (response: any) => {
        if (response.meta.success) {
          this.toast.success(USER_ROLE_MESSAGES.DELETE_USER, 'SUCCESS', 4000);
          this.loadUsers();
        }
      },
      error: (error: any) => {
        this.toast.danger(error.error.message, 'DANGER', 4000);
      },
    });
  }
}
